import React, { Component } from "react";
import {
  Chip,
  CircularProgress,
  Typography,
  WithStyles,
  createStyles,
  withStyles,
  Theme,
} from "@material-ui/core";
import { ExitToApp, MailOutline, AccountBalanceWallet } from "@material-ui/icons";
import gamesApi from "../../api/games";
import { LEAVE_REASON_LABELS, LEAVE_REASON_OPTIONS } from "../../types/gameLeave";

interface LeaveEvent {
  _id: string;
  playerName: string;
  gameCode?: string;
  gameName?: string;
  reasons?: string[];
  message?: string;
  createdAt?: string;
  wasPaid?: boolean;
  paidAmountPence?: number;
  creditStatus?:
    | "not_applicable"
    | "pending"
    | "issued"
    | "invalid_fee"
    | "unsupported_team_pricing"
    | "already_credited";
  initiatedBy?: "self" | "organiser";
  creditSummary?: {
    status: "open" | "redeeming" | "redeemed" | "expired";
    amountPence: number;
    remainingPence: number;
    expiresAt?: string;
    lastRedeemGameCode?: string;
  };
  notificationStatus?:
    | "pending"
    | "sending"
    | "retrying"
    | "sent"
    | "partial"
    | "failed"
    | "no_recipient"
    | "not_configured";
  notificationAttempts?: number;
  notificationNextAttemptAt?: string;
  notificationLastError?: string;
}

interface Props extends WithStyles<typeof styles> {
  gameId?: string;
}

interface State {
  loading: boolean;
  events: LeaveEvent[];
  error: string;
}

const REASON_LABELS: Record<string, string> = {
  ...LEAVE_REASON_LABELS,
  ...Object.fromEntries(
    LEAVE_REASON_OPTIONS.map(o => [o.key, o.label === "Other reason &/or leave message" ? "Other" : o.label]),
  ),
};

const reasonLabel = (key: string) => REASON_LABELS[key] || key;

/** Organiser-facing status only - no retry/error plumbing. */
const notificationStatusLabel = (event: LeaveEvent): string => {
  switch (event.notificationStatus) {
    case "pending":
    case "sending":
    case "retrying":
    case "partial":
      return "Email queued";
    case "sent":
      return "Email sent";
    case "not_configured":
      return "Email not configured";
    case "failed":
    case "no_recipient":
      return "Email not delivered";
    default:
      return "";
  }
};

const notificationTone = (
  status?: LeaveEvent["notificationStatus"],
): "ok" | "warn" | "error" | "muted" => {
  switch (status) {
    case "sent":
      return "ok";
    case "pending":
    case "sending":
    case "retrying":
    case "partial":
      return "warn";
    case "failed":
    case "no_recipient":
    case "not_configured":
      return "error";
    default:
      return "muted";
  }
};

const money = (pence: number) => `£${(pence / 100).toFixed(2)}`;

const creditLabel = (event: LeaveEvent) => {
  const summary = event.creditSummary;
  if (summary) {
    if (summary.status === "expired") {
      return `Credit expired (${money(summary.amountPence)})`;
    }
    if (summary.status === "redeemed") {
      const where = summary.lastRedeemGameCode ? ` on ${summary.lastRedeemGameCode}` : "";
      return `Credit used${where} (${money(summary.amountPence)})`;
    }
    if (summary.status === "open" || summary.status === "redeeming") {
      const same = summary.remainingPence === summary.amountPence;
      if (same) return `Credit open: ${money(summary.remainingPence)}`;
      return `Credit left: ${money(summary.remainingPence)} of ${money(summary.amountPence)}`;
    }
  }
  if (event.creditStatus === "issued" && typeof event.paidAmountPence === "number") {
    return `Credit issued: ${money(event.paidAmountPence)}`;
  }
  if (event.creditStatus === "pending" && typeof event.paidAmountPence === "number") {
    return `Credit pending: ${money(event.paidAmountPence)}`;
  }
  if (event.creditStatus === "unsupported_team_pricing") {
    return "Paid leave: no credit (team pricing)";
  }
  if (event.creditStatus === "invalid_fee") {
    return "Paid leave: credit not issued (no frozen fee / payment evidence)";
  }
  if (event.creditStatus === "already_credited") {
    return "Paid leave: no new credit (open credit already exists from this game)";
  }
  if (event.wasPaid && event.creditStatus === "not_applicable" && event.initiatedBy === "organiser") {
    return "Paid remove: no credit";
  }
  if (event.wasPaid) {
    return "Paid leave: no credit";
  }
  return "";
};

class GameLeaveHistory extends Component<Props, State> {
  state: State = { loading: true, events: [], error: "" };

  componentDidMount() {
    this.load();
  }

  load = () => {
    const { gameId } = this.props;
    this.setState({ loading: true, error: "" });
    const req = gameId ? gamesApi.getLeaveEvents(gameId) : gamesApi.getOrganiserLeaveEvents();
    req
      .then((res: any) => {
        const data = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
        this.setState({ events: Array.isArray(data) ? data : [], loading: false });
      })
      .catch(() => {
        this.setState({ loading: false, error: "Could not load leave history." });
      });
  };

  render() {
    const { classes } = this.props;
    const { loading, events, error } = this.state;

    if (loading) {
      return (
        <div className={classes.centered}>
          <CircularProgress size={28} />
        </div>
      );
    }

    if (error) {
      return (
        <div className={classes.pad}>
          <Typography color="error">{error}</Typography>
        </div>
      );
    }

    if (!events.length) {
      return (
        <div className={classes.empty}>
          <div className={classes.emptyIconWrap} aria-hidden>
            <ExitToApp className={classes.emptyIcon} />
          </div>
          <Typography className={classes.emptyTitle}>No leavers yet</Typography>
          <Typography className={classes.emptyBody} color="textSecondary">
            When a player leaves, or an admin removes them from the game, they show up here.
          </Typography>
        </div>
      );
    }

    return (
      <div className={classes.root}>
        <ul className={classes.list}>
          {events.map(ev => {
            const when = ev.createdAt ? new Date(ev.createdAt).toLocaleString() : "";
            const reasons = (ev.reasons || []).map(reasonLabel);
            const credit = creditLabel(ev);
            const notification =
              ev.initiatedBy === "organiser" ? "" : notificationStatusLabel(ev);
            const tone = notificationTone(ev.notificationStatus);
            const showGameMeta = !this.props.gameId && (ev.gameCode || ev.gameName);

            return (
              <li key={ev._id} className={classes.card}>
                <div className={classes.cardHead}>
                  <div className={classes.playerBlock}>
                    <Typography className={classes.playerName} component="h3">
                      {ev.playerName || "Player"}
                    </Typography>
                    {showGameMeta ? (
                      <Typography className={classes.gameMeta} color="textSecondary">
                        {[ev.gameCode, ev.gameName].filter(Boolean).join(" · ")}
                      </Typography>
                    ) : null}
                  </div>
                  {when ? (
                    <div className={classes.whenBlock}>
                      <Typography className={classes.whenLabel} component="p">
                        {ev.initiatedBy === "organiser" ? "Removed" : "Left"}
                      </Typography>
                      <Typography className={classes.when} component="time">
                        {when}
                      </Typography>
                    </div>
                  ) : null}
                </div>

                {reasons.length > 0 ? (
                  <div className={classes.section}>
                    <Typography className={classes.sectionLabel} component="p">
                      Reasons
                    </Typography>
                    <ul className={classes.reasonList}>
                      {reasons.map((label, i) => (
                        <li key={`${ev._id}-r-${i}`} className={classes.reasonItem}>
                          {label}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {ev.message ? (
                  <blockquote className={classes.quote}>
                    <Typography className={classes.quoteLabel} component="p">
                      Message
                    </Typography>
                    <Typography className={classes.quoteBody} component="p">
                      {ev.message}
                    </Typography>
                  </blockquote>
                ) : null}

                {(credit || notification) && (
                  <div className={classes.metaRow}>
                    {credit ? (
                      <Chip
                        size="small"
                        icon={<AccountBalanceWallet className={classes.chipIcon} />}
                        label={credit}
                        className={`${classes.chip} ${classes.chipCredit}`}
                      />
                    ) : null}
                    {notification ? (
                      <Chip
                        size="small"
                        icon={<MailOutline className={classes.chipIcon} />}
                        label={notification}
                        className={`${classes.chip} ${
                          tone === "ok"
                            ? classes.chipOk
                            : tone === "warn"
                            ? classes.chipWarn
                            : tone === "error"
                            ? classes.chipError
                            : classes.chipMuted
                        }`}
                      />
                    ) : null}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );
  }
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(1.5, 2, 2),
      [theme.breakpoints.up("sm")]: {
        padding: theme.spacing(2, 2.5, 2.5),
      },
    },
    pad: {
      padding: theme.spacing(2),
    },
    centered: {
      padding: theme.spacing(4),
      textAlign: "center",
    },
    empty: {
      padding: theme.spacing(4, 3),
      textAlign: "center",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: theme.spacing(1),
    },
    emptyIconWrap: {
      width: 52,
      height: 52,
      borderRadius: "50%",
      backgroundColor: "rgba(103, 58, 183, 0.08)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: theme.spacing(0.5),
    },
    emptyIcon: {
      color: theme.palette.primary.main,
      fontSize: 26,
    },
    emptyTitle: {
      fontWeight: 600,
      color: "#0f172a",
      letterSpacing: "-0.01em",
    },
    emptyBody: {
      maxWidth: 280,
      lineHeight: 1.45,
      fontSize: "0.875rem",
    },
    list: {
      listStyle: "none",
      margin: 0,
      padding: 0,
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1.5),
      maxHeight: 460,
      overflow: "auto",
    },
    card: {
      margin: 0,
      padding: theme.spacing(1.75, 2),
      borderRadius: 10,
      border: "1px solid rgba(226, 232, 240, 0.95)",
      backgroundColor: "#ffffff",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    },
    cardHead: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(1.25),
    },
    playerBlock: {
      minWidth: 0,
      flex: 1,
    },
    playerName: {
      fontWeight: 600,
      fontSize: "1rem",
      color: "#0f172a",
      letterSpacing: "-0.01em",
      lineHeight: 1.3,
    },
    gameMeta: {
      fontSize: "0.8125rem",
      marginTop: 2,
      lineHeight: 1.35,
    },
    whenBlock: {
      flexShrink: 0,
      textAlign: "right",
      maxWidth: "46%",
    },
    whenLabel: {
      margin: 0,
      marginBottom: 2,
      fontSize: "0.6875rem",
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: "#64748b",
      lineHeight: 1.2,
    },
    when: {
      display: "block",
      fontSize: "0.75rem",
      color: "#475569",
      lineHeight: 1.35,
    },
    section: {
      marginBottom: theme.spacing(1.25),
    },
    sectionLabel: {
      margin: 0,
      marginBottom: theme.spacing(0.5),
      fontSize: "0.6875rem",
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: "#64748b",
    },
    reasonList: {
      margin: 0,
      padding: 0,
      listStyle: "none",
      display: "flex",
      flexDirection: "column",
      gap: 4,
    },
    reasonItem: {
      position: "relative",
      paddingLeft: 14,
      fontSize: "0.875rem",
      color: "#1e293b",
      lineHeight: 1.4,
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: "0.55em",
        width: 6,
        height: 6,
        borderRadius: "50%",
        backgroundColor: theme.palette.primary.main,
        opacity: 0.75,
      },
    },
    quote: {
      margin: 0,
      marginBottom: theme.spacing(1.25),
      padding: theme.spacing(1.25, 1.5),
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      backgroundColor: "rgba(103, 58, 183, 0.06)",
      borderRadius: "0 8px 8px 0",
    },
    quoteLabel: {
      margin: 0,
      marginBottom: 4,
      fontSize: "0.6875rem",
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: theme.palette.primary.main,
    },
    quoteBody: {
      margin: 0,
      fontSize: "0.875rem",
      color: "#1e293b",
      lineHeight: 1.5,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    },
    metaRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(0.75),
      marginTop: theme.spacing(0.25),
    },
    chip: {
      height: 26,
      fontSize: "0.75rem",
      fontWeight: 500,
      borderRadius: 6,
      maxWidth: "100%",
      "& .MuiChip-label": {
        paddingLeft: 6,
        paddingRight: 8,
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      "& .MuiChip-icon": {
        marginLeft: 6,
        marginRight: -2,
      },
    },
    chipIcon: {
      fontSize: "0.95rem !important",
    },
    chipCredit: {
      backgroundColor: "rgba(16, 185, 129, 0.12)",
      color: "#065f46",
      "& .MuiChip-icon": { color: "#059669" },
    },
    chipOk: {
      backgroundColor: "rgba(16, 185, 129, 0.12)",
      color: "#065f46",
      "& .MuiChip-icon": { color: "#059669" },
    },
    chipWarn: {
      backgroundColor: "rgba(245, 158, 11, 0.14)",
      color: "#92400e",
      "& .MuiChip-icon": { color: "#d97706" },
    },
    chipError: {
      backgroundColor: "rgba(239, 68, 68, 0.12)",
      color: "#991b1b",
      "& .MuiChip-icon": { color: "#dc2626" },
    },
    chipMuted: {
      backgroundColor: "rgba(148, 163, 184, 0.18)",
      color: "#475569",
      "& .MuiChip-icon": { color: "#64748b" },
    },
  });

export default withStyles(styles)(GameLeaveHistory);
